/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import {
  MetricDefinition,
  MetricName,
  MetricNameFragment,
  MetricNameFragments,
  MetricNameFragmentsHash,
} from '../types/metrics';

const METRIC_TEMPLATE_ANY = '*';
export const METRIC_TEMPLATE_TIER_SEPARATOR = '.';

export const templateHasWildcards = (template: MetricNameFragments): boolean =>
  template.some((fragment) => metricNameFragmentToString(fragment).includes(METRIC_TEMPLATE_ANY));

export const getMetricNameFragments = (name: MetricName): MetricNameFragments =>
  toMetricNameFragments(...metricNameToString(name).split(METRIC_TEMPLATE_TIER_SEPARATOR));

export const joinMetricNameFragments = (fragments: MetricNameFragments): MetricName =>
  stringToMetricName(fragments.map(metricNameFragmentToString).join(METRIC_TEMPLATE_TIER_SEPARATOR));
export const matchMetricNameFragments = (a: MetricNameFragments, b: MetricNameFragments): boolean => {
  return (
    a.length === b.length &&
    a.every((fragment, i) => {
      const bee = b[i];
      return (
        (!!bee && metricNameFragmentToString(bee) === METRIC_TEMPLATE_ANY) ||
        metricNameFragmentToString(fragment) === METRIC_TEMPLATE_ANY ||
        (!!bee && bee === fragment)
      );
    })
  );
};
export const queryMetricDefs = (templates: MetricNameFragments[], defs: MetricDefinition[]): MetricDefinition[] => {
  return defs.filter((def) => {
    return templates.some((template) => matchMetricNameFragments(def.tiers, template));
  });
};

export const metricNameToString = (name: MetricName): string => name as unknown as string;
export const stringToMetricName = (name: string): MetricName => name as unknown as MetricName;

export const metricNameFragmentToString = (fragment: MetricNameFragment): string => fragment as unknown as string;
export const stringToMetricNameFragment = (fragment: string): MetricNameFragment =>
  fragment as unknown as MetricNameFragment;
export const toMetricNameFragments = (...fragments: (string | MetricNameFragment)[]): MetricNameFragments =>
  fragments.map((fragment) =>
    stringToMetricNameFragment(typeof fragment === 'string' ? fragment : metricNameFragmentToString(fragment)),
  );

export const hashMetricNameFragments = (value: MetricNameFragments) =>
  JSON.stringify(value) as unknown as MetricNameFragmentsHash;
export const metricNameFragmentsHashToString = (value: MetricNameFragmentsHash) => value as unknown as string;
